import { workflowAutoPauseFailureThreshold } from "../workflows/vocabulary.js"

/**
 * The words an agent is written in.
 *
 * An agent is one standing decision: when this event happens, apply the
 * workspace's policy and either act or hold with named reasons. Every list
 * here is the single source of truth the engine, the tables, the routes and
 * the settings card all read, so none of them can offer a word the others do
 * not understand. The registry in ./registry.ts binds these words to each
 * kind's schema and evaluator.
 */

export const agentKinds = ["bills", "statements"] as const

export type AgentKind = (typeof agentKinds)[number]

/**
 * `watch` sits between off and on so automation is earned rather than
 * assumed: the agent evaluates every document and records the verdict it
 * would give, but acts on nothing. New configurations start here.
 */
export const agentStatuses = ["off", "watch", "on"] as const

export type AgentStatus = (typeof agentStatuses)[number]

/**
 * What one evaluation did. `approved` writes a real document approval;
 * `held` and `would_approve` write only the action row. A document with any
 * action row is never decided again, which is what keeps an undone approval
 * undone.
 */
export const agentActionTypes = ["approved", "held", "would_approve"] as const

export type AgentActionType = (typeof agentActionTypes)[number]

/** What happens to a bill after the bills agent approves it. The statements
 * agent has no after-approval action: an approved statement is a statement
 * marked reviewed, and delivery stays with workflows and exports. */
export const agentAfterApprovals = ["keep", "post_to_quickbooks"] as const

export type AgentAfterApproval = (typeof agentAfterApprovals)[number]

/** Why we paused an agent ourselves. One reason today; the column is text in
 * the same posture as the workflow pause vocabulary. */
export const agentSystemPauseReasons = ["consecutive_failures"] as const

export type AgentSystemPauseReason = (typeof agentSystemPauseReasons)[number]

/**
 * Consecutive engine failures before the agent pauses itself and raises a
 * notification. The same number the workflows guardrail uses, read from the
 * same constant so the two backstops cannot drift apart.
 */
export const agentAutoPauseFailureThreshold = workflowAutoPauseFailureThreshold

/**
 * Approvals one engine pass may write. A bounded pass is the blast-radius
 * ceiling: a policy that would approve a whole backlog does it across
 * passes, each one small enough to stop.
 */
export const agentMaxApprovalsPerPass = 20

/**
 * The gates the bills policy walks, in the order a reviewer reads them.
 * The first three are tuned by the policy card's controls; the last two are
 * fixed and conservative.
 */
export const billsGateNames = [
  "checks",
  "known_vendor",
  "approval_limit",
  "duplicate",
  "matching",
] as const

export type BillsGateName = (typeof billsGateNames)[number]

/**
 * The gates the statements policy walks, in the order a reviewer reads them:
 * does the statement add up, is it an account we know, does it follow the
 * last period, is it the only copy, and did anything stay flagged. The first
 * two are tuned by the policy card's controls; the last three are fixed and
 * conservative.
 */
export const statementsGateNames = [
  "reconciled",
  "known_account",
  "continuity",
  "duplicate",
  "clean_flags",
] as const

export type StatementsGateName = (typeof statementsGateNames)[number]

/**
 * Not a gate. The name an evidence entry carries when an approval is finished
 * after an interrupted pass and the facts behind the original decision can no
 * longer be read. An action row's reasons are frozen for ever, so the one
 * thing a recovery may not write is an empty list.
 */
export const agentRecoveredEvidenceName = "recovered"

/**
 * Every gate name any kind can record, in registry order. The wire schema
 * reads this list rather than one kind's, so a second agent's evidence is not
 * rejected on the way to the client.
 */
export const agentGateNames = [
  ...billsGateNames,
  ...statementsGateNames,
  agentRecoveredEvidenceName,
] as const

export type AgentGateName = (typeof agentGateNames)[number]

/**
 * One gate's result with the values it compared, so an audit answer shows
 * evidence rather than a green tick. The same five-field shape as
 * `ApEvidence`, keyed by gate rather than by matcher check, because the
 * frozen `agent_actions.reasons` must read the same way the payables
 * evidence panel does.
 */
export type AgentGateEvidence = {
  readonly gate: AgentGateName
  readonly outcome: "passed" | "failed" | "not_available"
  /** Sentence case, house voice. The failed sentence is the held chip. */
  readonly label: string
  readonly expected: string | null
  readonly actual: string | null
}

/**
 * The evidence a recovered approval records when the gates cannot be read
 * again. The approval itself already stands; this says why the trail behind
 * it is thin.
 */
export const recoveredAgentEvidence = (): AgentGateEvidence => ({
  gate: agentRecoveredEvidenceName,
  outcome: "not_available",
  label:
    "LedgerBox finished this approval after an interruption and could not read the checks again.",
  expected: null,
  actual: null,
})

/** Act or hold. Every kind reaches one of these two, and only these two. */
export type AgentVerdictAction = "approve" | "hold"

export type BillsVerdictAction = AgentVerdictAction

/**
 * What the policy concluded about one bill: act or hold, plus every gate's
 * evidence. `reasons` always carries all five gates, passed ones included,
 * because "all 5 gates passed" is a claim the activity feed has to be able
 * to show its work for.
 */
export type BillsVerdict = {
  readonly action: AgentVerdictAction
  readonly reasons: readonly AgentGateEvidence[]
}

/**
 * What the policy concluded about one statement. The same shape as the bills
 * verdict on purpose: the engine, the action rows and the activity feed read
 * one thing, so a third kind adds no branch to any of them.
 */
export type StatementsVerdict = {
  readonly action: AgentVerdictAction
  readonly reasons: readonly AgentGateEvidence[]
}
